import { useEffect, useRef, useState } from 'react'
import { X, Loader2, Download } from 'lucide-react'
import api from '../../services/api'
import i18n from '../../i18n.js'

// Build a whitespace-normalized index of a rendered element's text, mapping each
// normalized character back to its DOM (textNode, offset). Lets us find a quoted
// passage even when it spans multiple paragraphs/runs, then build a live Range.
function buildNormIndex(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let norm = ''
  const map = []
  let node, first = true
  while ((node = walker.nextNode())) {
    const text = node.nodeValue || ''
    if (!first && norm && norm[norm.length - 1] !== ' ') { norm += ' '; map.push({ node, offset: 0 }) }
    first = false
    let prevSpace = norm ? norm[norm.length - 1] === ' ' : true
    for (let i = 0; i < text.length; i++) {
      const sp = /\s/.test(text[i])
      if (sp) { if (prevSpace) continue; norm += ' '; map.push({ node, offset: i }); prevSpace = true }
      else { norm += text[i]; map.push({ node, offset: i }); prevSpace = false }
    }
  }
  return { norm, map }
}

function rangeForText(idx, needle) {
  const nn = (needle || '').replace(/\s+/g, ' ').trim()
  if (!nn) return null
  let s, e
  const pos = idx.norm.indexOf(nn)
  if (pos >= 0) {
    s = idx.map[pos]; e = idx.map[pos + nn.length - 1]
  } else {
    // A PDF's text layer splits a line into pieces, and a selection can join two pieces with no
    // space where the page index puts one. Match again with spaces ignored on both sides.
    if (!idx.compact) {
      let cs = ''; const at = []
      for (let i = 0; i < idx.norm.length; i++) if (idx.norm[i] !== ' ') { cs += idx.norm[i]; at.push(i) }
      idx.compact = { cs, at }
    }
    const want = nn.replace(/ /g, '')
    const p2 = want ? idx.compact.cs.indexOf(want) : -1
    if (p2 < 0) return null
    s = idx.map[idx.compact.at[p2]]; e = idx.map[idx.compact.at[p2 + want.length - 1]]
  }
  if (!s || !e) return null
  try {
    const r = document.createRange()
    r.setStart(s.node, s.offset)
    r.setEnd(e.node, e.offset + 1)
    return r
  } catch { return null }
}

const HL_SUPPORTED = typeof window !== 'undefined' && typeof window.Highlight !== 'undefined'
  && typeof CSS !== 'undefined' && CSS.highlights

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

// Excel colours arrive as ARGB ('FF4472C4'). Theme colours arrive as {theme, tint}, which can
// only be resolved against the workbook's theme XML — so those are left alone rather than
// guessed at, which would put the wrong colour on the page with total confidence.
const argb = (c) => {
  const v = c?.argb
  return (typeof v === 'string' && v.length === 8) ? '#' + v.slice(2) : null
}

const BORDER_WIDTH = { hair: '0.5px', thin: '1px', medium: '2px', thick: '3px' }

function borderCss(side) {
  if (!side?.style) return null
  const w = BORDER_WIDTH[side.style] || '1px'
  const kind = side.style === 'dotted' ? 'dotted'
    : (side.style === 'dashed' || side.style === 'dashDot' || side.style === 'dashDotDot') ? 'dashed'
    : side.style === 'double' ? 'double' : 'solid'
  return `${w} ${kind} ${argb(side.color) || '#9ca3af'}`
}

/**
 * A worksheet, as close to what the person built as a browser can show: their column widths,
 * row heights, merged cells, fills, fonts, borders and alignment.
 *
 * Rendered from ExcelJS rather than a plain values-to-table dump, because a spreadsheet's
 * formatting is frequently the meaning — a red cell is a flag, a merged header is a grouping,
 * and stripping them hands the reader a different document.
 */
async function renderWorkbook(el, arrayBuffer) {
  const mod = await import('exceljs')
  const ExcelJS = mod.default || mod
  const wb = new ExcelJS.Workbook()
  await wb.xlsx.load(arrayBuffer)

  const sheets = []
  wb.eachSheet((ws) => {
    // Merged ranges, keyed by their top-left cell — that cell carries the span, the rest are
    // not drawn at all.
    const spans = {}
    const covered = new Set()
    for (const range of (ws.model?.merges || [])) {
      const m = /^([A-Z]+)(\d+):([A-Z]+)(\d+)$/.exec(range)
      if (!m) continue
      const colNum = (s) => [...s].reduce((n, ch) => n * 26 + (ch.charCodeAt(0) - 64), 0)
      const c1 = colNum(m[1]), r1 = +m[2], c2 = colNum(m[3]), r2 = +m[4]
      spans[`${r1}:${c1}`] = { rowspan: r2 - r1 + 1, colspan: c2 - c1 + 1 }
      for (let r = r1; r <= r2; r++) {
        for (let c = c1; c <= c2; c++) if (!(r === r1 && c === c1)) covered.add(`${r}:${c}`)
      }
    }

    const lastCol = ws.columnCount || 0
    const cols = []
    for (let c = 1; c <= lastCol; c++) {
      const w = ws.getColumn(c)?.width
      // Excel measures a column in characters of the default font; ~7px each plus the padding.
      cols.push(`<col style="width:${w ? Math.round(w * 7 + 5) : 80}px">`)
    }

    const rows = []
    ws.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      const cells = []
      for (let c = 1; c <= lastCol; c++) {
        if (covered.has(`${rowNumber}:${c}`)) continue
        const cell = row.getCell(c)
        const span = spans[`${rowNumber}:${c}`]

        const style = []
        const fill = cell.fill
        if (fill?.type === 'pattern' && fill.pattern === 'solid') {
          const bg = argb(fill.fgColor)
          if (bg) style.push(`background:${bg}`)
        }
        const f = cell.font
        if (f?.bold) style.push('font-weight:600')
        if (f?.italic) style.push('font-style:italic')
        if (f?.underline) style.push('text-decoration:underline')
        if (f?.size) style.push(`font-size:${f.size}pt`)
        if (f?.name) style.push(`font-family:'${f.name.replace(/'/g, '')}',sans-serif`)
        const fc = argb(f?.color)
        if (fc) style.push(`color:${fc}`)

        const a = cell.alignment
        if (a?.horizontal) style.push(`text-align:${a.horizontal}`)
        if (a?.vertical) style.push(`vertical-align:${a.vertical === 'middle' ? 'middle' : a.vertical}`)
        if (a?.wrapText) style.push('white-space:pre-wrap')
        else style.push('white-space:nowrap')

        for (const [side, prop] of [['top', 'border-top'], ['left', 'border-left'],
                                    ['bottom', 'border-bottom'], ['right', 'border-right']]) {
          const b = borderCss(cell.border?.[side])
          if (b) style.push(`${prop}:${b}`)
        }

        let text = cell.text ?? ''
        if (cell.value instanceof Date) text = cell.value.toLocaleDateString()
        else if (cell.value && typeof cell.value === 'object' && 'result' in cell.value) {
          text = cell.value.result ?? ''            // a formula shows its answer, as Excel does
        }

        cells.push(`<td${span ? ` rowspan="${span.rowspan}" colspan="${span.colspan}"` : ''}`
          + ` style="${style.join(';')}">${esc(text).replace(/\n/g, '<br>')}</td>`)
      }
      const h = row.height ? ` style="height:${Math.round(row.height * 1.333)}px"` : ''
      rows.push(`<tr${h}>${cells.join('')}</tr>`)
    })

    sheets.push(
      `<section class="xl-sheet">`
      + `<h4 class="xl-name">${esc(ws.name)}</h4>`
      + `<div class="xl-scroll"><table class="xl-table"><colgroup>${cols.join('')}</colgroup>`
      + `<tbody>${rows.join('')}</tbody></table></div></section>`
    )
  })

  el.innerHTML = sheets.join('') || `<p class="xl-empty">${i18n.t('ui:no_sheets')}</p>`
}

/**
 * In-app file viewer. Fetches the file (authenticated) and renders it INLINE, keeping the
 * uploaded document's own formatting wherever a browser can be made to:
 *   • PDF          → embedded iframe (the browser's own renderer)
 *   • .docx        → docx-preview: real pages, fonts, tables, images, headers/footers
 *   • .xlsx        → ExcelJS: the sheet's own widths, fills, borders, merges and fonts
 *   • .pptx        → pptx-preview: the slides, laid out as slides
 *   • .xls / .doc  → older binary formats; .xls falls back to a plain table, .doc to download
 *   • image        → <img>
 *   • video/audio  → <video>/<audio>
 *   • text/csv/md  → <pre>
 *   • anything else→ graceful "download to open" fallback
 *
 * A note on why this is not just mammoth: converting a Word file to clean HTML throws away the
 * layout that made it a document — the columns, the page breaks, the table rules, the sizes
 * somebody chose. People check a file against what they sent, so the viewer has to show that,
 * not a readable approximation of it.
 *
 * Props:
 *   open, onClose, fetchUrl (the download endpoint), filename, onDownload (optional),
 *   sidePanel (optional) — JSX rendered in a right-hand column beside the document,
 *   e.g. a comment thread, so the team can read the sheet and comment side-by-side.
 *   onTextSelect (optional) — called on mouse-up over the document; lets the parent
 *   capture the reader's current text selection (for anchored, MS-Word-style comments).
 *   highlights (optional) — [{id, text, colorKey}] passages to highlight; colorKey groups
 *     them by author so each person's comments get a distinct colour.
 *   palette (optional) — { colorKey: cssColor } used to paint each group.
 *   activeHighlightId (optional) — the highlight to emphasize + scroll into view
 *   (e.g. when the reader clicks the matching comment card).
 */
export default function FileViewerModal({ open, onClose, fetchUrl, filename, onDownload, sidePanel, onTextSelect, highlights, palette, activeHighlightId, activeColor }) {
  const [st, setSt] = useState({ loading: true })
  const [painted, setPainted] = useState(0)   // bumps when a mounted renderer finishes drawing
  const contentRef = useRef(null)
  const mountRef = useRef(null)
  const rangeMapRef = useRef({})
  const highlightsKey = (highlights || []).map(h => `${h.id}:${h.colorKey}:${h.text}`).join('|')
  const paletteKey = JSON.stringify(palette || {})
  const styleText = Object.entries(palette || {}).map(([k, c]) => `::highlight(pt-${k}){background-color:${c}}`).join('')
    + `::highlight(pt-active){background-color:${activeColor || 'rgba(250,204,21,.85)'};text-decoration:underline;text-decoration-thickness:2px;text-decoration-color:#b45309}`
    // The worksheet's own look. Deliberately minimal — every colour, width and rule on a cell
    // comes from the file, so anything set here would be overriding the author.
    + `.xl-sheet{margin:0 0 24px}.xl-name{font:600 12px/1.6 system-ui;color:#0f766e;text-transform:uppercase;letter-spacing:.05em;margin:0 0 6px}`
    + `.xl-scroll{overflow-x:auto;background:#fff;border:1px solid #e5e7eb;border-radius:8px}`
    + `.xl-table{border-collapse:collapse;font:13px/1.4 Calibri,system-ui,sans-serif}`
    + `.xl-table td{border:1px solid #f1f5f9;padding:2px 6px;vertical-align:bottom}`
    + `.xl-empty{color:#9ca3af;font-size:13px}`
    + `.mammoth-fallback{background:#fff;max-width:816px;margin:16px auto;padding:48px;border:1px solid #e5e7eb;`
    + `border-radius:4px;font:15px/1.7 Calibri,Georgia,serif;color:#111827}`
    + `.mammoth-fallback h1,.mammoth-fallback h2,.mammoth-fallback h3{font-weight:700;margin:1.2em 0 .4em}`
    + `.mammoth-fallback p{margin:0 0 .8em}.mammoth-fallback table{border-collapse:collapse;width:100%}`
    + `.mammoth-fallback td,.mammoth-fallback th{border:1px solid #d1d5db;padding:4px 8px}`

  useEffect(() => {
    if (!open || !fetchUrl) return
    let objectUrl = null
    let cancelled = false
    setSt({ loading: true })
    setPainted(0)
    ;(async () => {
      try {
        const r = await api.get(fetchUrl, { params: { disposition: 'inline' }, responseType: 'blob' })
        let blob = r.data
        const name = (filename || 'file')
        const ext = (name.split('.').pop() || '').toLowerCase()
        // S3/GCS-backed files return JSON with a presigned URL instead of the bytes. For
        // formats the BROWSER renders natively (pdf/image/video/audio), just open the URL —
        // the browser shows it inline on its own. For Office formats and text we do our own
        // in-app rendering, so the browser can't be trusted to "view" those — Chrome has no
        // renderer for them and downloads instead. So for those we fetch the actual bytes
        // from the presigned URL and fall through to the same rendering logic below.
        if (r.headers['content-type']?.includes('application/json')) {
          const j = JSON.parse(await blob.text())
          if (j?.url) {
            const NATIVE_EXTS = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'mp4', 'webm', 'mov', 'ogg', 'mp3', 'wav', 'm4a']
            if (NATIVE_EXTS.includes(ext)) { window.open(j.url, '_blank'); onClose(); return }
            const resp = await fetch(j.url)
            if (!resp.ok) throw new Error(`fetch failed: ${resp.status}`)
            blob = await resp.blob()
          }
        }
        if (cancelled) return
        const mime = blob.type || ''
        // Some places know what they are showing only by its bytes — a CV fetched from
        // /users/{id}/cv arrives with no name at all. The Office MIME types are unambiguous,
        // so a file is recognised by either its extension or what the server called it.
        const OFFICE_MIME = {
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
          'application/msword': 'doc',
          'application/vnd.ms-excel': 'xls',
        }
        const kind = OFFICE_MIME[mime] || ext

        if ((ext === 'pdf' || mime.includes('pdf')) && onTextSelect) {
          // A viewer that takes comments on a passage must be able to read what is selected, which
          // the browser's own PDF viewer (inside a frame) never lets the page do. Drawn into the
          // page instead, with a text layer, a PDF behaves like a Word file here.
          const { renderPdfInto } = await import('./renderPdf.js')
          setSt({
            loading: false, kind: 'mount',
            render: (el) => renderPdfInto(el, blob),
            // If it cannot be drawn (a damaged or protected file) the browser's viewer still can.
            fallback: async () => {
              objectUrl = URL.createObjectURL(blob)
              setSt({ loading: false, kind: 'pdf', url: objectUrl })
            },
          })
        } else if (ext === 'pdf' || mime.includes('pdf')) {
          objectUrl = URL.createObjectURL(blob); setSt({ loading: false, kind: 'pdf', url: objectUrl })
        } else if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext) || mime.startsWith('image/')) {
          objectUrl = URL.createObjectURL(blob); setSt({ loading: false, kind: 'image', url: objectUrl })
        } else if (['mp4', 'webm', 'mov', 'ogg'].includes(ext) || mime.startsWith('video/')) {
          objectUrl = URL.createObjectURL(blob); setSt({ loading: false, kind: 'video', url: objectUrl })
        } else if (['mp3', 'wav', 'm4a'].includes(ext) || mime.startsWith('audio/')) {
          objectUrl = URL.createObjectURL(blob); setSt({ loading: false, kind: 'audio', url: objectUrl })
        } else if (['txt', 'csv', 'log', 'json', 'md'].includes(ext) || mime.startsWith('text/')) {
          setSt({ loading: false, kind: 'text', text: await blob.text() })

        } else if (kind === 'docx') {
          // The document as Word lays it out — pages, headers, footers, tables, images.
          const docx = await import('docx-preview')
          setSt({
            loading: false, kind: 'mount',
            render: (el) => docx.renderAsync(blob, el, undefined, {
              className: 'docx', inWrapper: true, breakPages: true,
              ignoreWidth: false, ignoreHeight: false, ignoreFonts: false,
              renderHeaders: true, renderFooters: true,
              renderFootnotes: true, renderEndnotes: true,
              useBase64URL: true, experimental: true,
            }),
            // If the file is one docx-preview cannot parse, the reader still gets the words
            // rather than an error — a degraded view beats no view.
            fallback: async (el) => {
              const mammoth = await import('mammoth')
              const res = await mammoth.convertToHtml({ arrayBuffer: await blob.arrayBuffer() })
              el.innerHTML = `<div class="mammoth-fallback">${res.value || '<p>(empty document)</p>'}</div>`
            },
          })

        } else if (kind === 'xlsx') {
          const buf = await blob.arrayBuffer()
          setSt({
            loading: false, kind: 'mount', pad: true,
            render: (el) => renderWorkbook(el, buf),
            fallback: async (el) => {
              const XLSX = await import('xlsx')
              const wb = XLSX.read(buf, { type: 'array' })
              el.innerHTML = wb.SheetNames.map(n =>
                `<section class="xl-sheet"><h4 class="xl-name">${esc(n)}</h4>`
                + `<div class="xl-scroll">${XLSX.utils.sheet_to_html(wb.Sheets[n])}</div></section>`).join('')
            },
          })

        } else if (kind === 'xls') {
          // The old binary format — ExcelJS doesn't read it, so its values are shown plainly.
          const XLSX = await import('xlsx')
          const wb = XLSX.read(await blob.arrayBuffer(), { type: 'array' })
          const html = wb.SheetNames.map(n =>
            `<section class="xl-sheet"><h4 class="xl-name">${esc(n)}</h4>`
            + `<div class="xl-scroll">${XLSX.utils.sheet_to_html(wb.Sheets[n])}</div></section>`).join('')
          setSt({ loading: false, kind: 'html', html })

        } else if (kind === 'pptx') {
          const buf = await blob.arrayBuffer()
          const { init } = await import('pptx-preview')
          setSt({
            loading: false, kind: 'mount', pad: true,
            render: async (el) => {
              const width = Math.max(320, (el.clientWidth || 900) - 8)
              // Every slide down the page rather than one at a time: people read a deck here
              // to check it, and scrolling beats clicking through it.
              const previewer = init(el, { width, height: Math.round(width * 0.5625), mode: 'list' })
              await previewer.preview(buf)
            },
          })

        } else {
          setSt({ loading: false, kind: 'unsupported' })
        }
      } catch (e) {
        if (!cancelled) setSt({ loading: false, error: 'Could not load a preview for this file.', kind: 'unsupported' })
      }
    })()
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [open, fetchUrl, filename])

  // Renderers that draw into a real DOM node (Word, Excel, PowerPoint) rather than handing
  // back an HTML string. Runs once the node exists, and tells the highlighter when it is done.
  useEffect(() => {
    if (st.kind !== 'mount' || !st.render) return
    const el = mountRef.current
    if (!el) return
    let cancelled = false
    el.innerHTML = ''
    ;(async () => {
      try {
        await st.render(el)
      } catch {
        if (cancelled) return
        // No fallback, or the fallback failed too: stop pretending and offer the file itself,
        // which is the thing the reader actually wanted.
        const giveUp = () => setSt({
          loading: false, kind: 'unsupported',
          error: 'This file could not be displayed in the browser — it may be an unusual or '
               + 'damaged version of the format.',
        })
        if (!st.fallback) { giveUp(); return }
        try { el.innerHTML = ''; await st.fallback(el) }
        catch { if (!cancelled) giveUp(); return }
      }
      if (!cancelled) setPainted(n => n + 1)
    })()
    return () => { cancelled = true }
  }, [st.kind, st.render])

  const clearHighlights = () => {
    if (!HL_SUPPORTED) return
    for (const k of [...CSS.highlights.keys()]) { if (k.startsWith('pt-')) CSS.highlights.delete(k) }
  }

  // Repaint ALL anchored passages (grouped by author colour) whenever the document,
  // the comment set, or the active selection changes — then emphasize + scroll to the
  // active one. Rebuilding every time is what keeps every highlight visible after a
  // card is clicked (a click only changes which one is *emphasized*, never hides the rest).
  useEffect(() => {
    const root = contentRef.current
    const renderable = st.kind === 'html' || st.kind === 'text' || (st.kind === 'mount' && painted > 0)
    if (!root || !renderable) { clearHighlights(); return }
    const idx = buildNormIndex(root)
    rangeMapRef.current = {}
    const groups = {}
    ;(highlights || []).forEach(h => {
      const r = rangeForText(idx, h.text)
      if (!r) return
      rangeMapRef.current[h.id] = r
      ;(groups[h.colorKey || 'self'] = groups[h.colorKey || 'self'] || []).push(r)
    })
    if (HL_SUPPORTED) {
      clearHighlights()
      Object.entries(groups).forEach(([key, ranges]) => {
        const hl = new window.Highlight(); ranges.forEach(r => hl.add(r))
        CSS.highlights.set('pt-' + key, hl)
      })
    }
    const active = activeHighlightId != null ? rangeMapRef.current[activeHighlightId] : null
    if (active) {
      if (HL_SUPPORTED) {
        const ah = new window.Highlight(); ah.add(active); ah.priority = 1
        CSS.highlights.set('pt-active', ah)
      }
      const el = active.startContainer?.parentElement
      if (el?.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
    return () => clearHighlights()
  }, [st.kind, st.html, st.text, painted, highlightsKey, paletteKey, activeHighlightId])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <style>{styleText}</style>
      {/* Wider with a panel beside it: a Word page is 8.5 inches, and squeezing one to fit a
          comment column is the same as not showing the layout at all. */}
      <div className={`bg-white rounded-xl shadow-2xl w-full ${sidePanel ? 'max-w-7xl' : 'max-w-5xl'} h-[90vh] flex flex-col overflow-hidden`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 shrink-0">
          <p className="text-sm font-semibold text-gray-800 truncate">{filename || 'File'}</p>
          <div className="flex items-center gap-2">
            {onDownload && (
              <button onClick={onDownload} className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg px-2.5 py-1">
                <Download className="w-3.5 h-3.5" /> Download
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="flex-1 flex min-h-0">
        <div ref={contentRef} className="flex-1 overflow-auto bg-gray-50" onMouseUp={onTextSelect}>
          {st.loading && (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 gap-2">
              <Loader2 className="w-6 h-6 animate-spin" /> Loading preview…
            </div>
          )}
          {st.error && st.kind !== 'mount' && st.kind !== 'unsupported' && <div className="h-full flex items-center justify-center text-red-500 text-sm">{st.error}</div>}
          {st.kind === 'pdf' && <iframe title={i18n.t('ui:iframe_preview')} src={st.url} className="w-full h-full border-0" />}
          {st.kind === 'image' && <div className="p-4 flex justify-center"><img src={st.url} alt={filename} className="max-w-full max-h-full object-contain" /></div>}
          {st.kind === 'video' && <div className="p-4 flex justify-center"><video src={st.url} controls className="max-w-full max-h-full" /></div>}
          {st.kind === 'audio' && <div className="p-6 flex justify-center"><audio src={st.url} controls /></div>}
          {st.kind === 'text' && <pre className="p-4 text-xs whitespace-pre-wrap text-gray-800">{st.text}</pre>}
          {st.kind === 'html' && <div className="p-6 bg-white m-4 rounded-lg border border-gray-200 prose prose-sm max-w-none [&_table]:border-collapse [&_td]:border [&_td]:border-gray-300 [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-gray-300 [&_th]:px-2 [&_th]:py-1" dangerouslySetInnerHTML={{ __html: st.html }} />}
          {st.kind === 'mount' && (
            <>
              {st.error && <p className="m-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{st.error}</p>}
              {/* No `prose` and no styling of our own: the file brings its own. */}
              <div ref={mountRef} className={st.pad ? 'p-4' : ''} />
            </>
          )}
          {st.kind === 'unsupported' && (
            <div className="h-full flex flex-col items-center justify-center text-gray-500 gap-3 text-center px-6">
              <p className="text-sm max-w-md">{st.error || "This file type can't be previewed in the browser."}</p>
              {onDownload && (
                <button onClick={onDownload} className="flex items-center gap-1.5 bg-blue-600 text-white rounded-lg px-4 py-2 text-sm hover:bg-blue-700">
                  <Download className="w-4 h-4" /> Download to open
                </button>
              )}
            </div>
          )}
        </div>

        {/* Optional side panel — e.g. the document's comment thread, so the team can
            read the sheet and discuss it in the same window. */}
        {sidePanel && (
          <div className="w-80 shrink-0 border-l border-gray-200 bg-white flex flex-col min-h-0">
            {sidePanel}
          </div>
        )}
        </div>
      </div>
    </div>
  )
}
